/**
 * WorldCamera — viewport adaptation for a fixed SceneWorld.
 *
 * Pipeline: SceneWorld → WorldCamera → Renderer
 *
 * Desktop resize rule:
 *   - World pixel box is fixed.
 *   - Design zoom is fixed (computed once for the reference viewport).
 *   - Viewport shrinks → crop (see less); grows → see more.
 *   - Zoom bumps UP only when the viewport would otherwise letterbox.
 *
 * Fixed-world CSS pan:
 *   translate(%) is resolved against the unscaled world plate; scale(zoom)
 *   runs after. Offsets are NOT multiplied by zoom or playfield factors.
 */

import {
  cameraPanTranslatePercent,
  clampCoverPanOffsetPercent,
  coverSafeCenterRange,
  visibleWorldHalfExtents,
  type ExplorationCameraMode,
  type ExploreZoomResult,
  DEFAULT_RESPONSIVE_EXPLORE,
} from '@/world-prototype/camera/responsiveExploreZoom';
import {
  resolveDesktopDesignZoom,
  worldFactorsForViewport,
  type SceneWorld,
} from '@/world-prototype/camera/sceneWorld';

export interface ViewportSize {
  width: number;
  height: number;
}

export interface WorldCameraFrame {
  /** CSS scale applied to the fixed world layer. */
  zoom: number;
  focusZoom: number;
  mode: ExplorationCameraMode;
  /**
   * Default composition center in world 0–1.
   * Gameplay follow seeds from this then pans with the character;
   * editor / locked framing may keep it fixed.
   */
  frameCenter: { x: number; y: number } | null;
  /**
   * Factors for PresenceController pan clamps:
   *   factor = worldPx / viewportPx
   */
  framingWidth: number;
  framingHeight: number;
  /** True when zoom had to rise above design zoom to fill a larger viewport. */
  coverBumped: boolean;
  config: ExploreZoomResult['config'];
}

/**
 * Dead-zone size as a fraction of the visible viewport for backyard follow.
 * Character drifts inside this soft box before the camera pans.
 *
 * Desktop values are locked — do not change (desktop framing is final).
 */
export const BACKYARD_CAMERA_FOLLOW = {
  desktop: {
    deadZoneViewportFracX: 0.6,
    deadZoneViewportFracY: 0.55,
  },
  /** @deprecated Prefer MOBILE_CAMERA.portraitFollow — kept for import stability. */
  mobile: {
    deadZoneViewportFracX: 0.55,
    deadZoneViewportFracY: 0.58,
  },
} as const;

/** Mobile-only camera mode: portrait follows; landscape fits the whole plate. */
export type MobileCameraMode = 'follow' | 'fit';

/**
 * Mobile portrait vs landscape camera policy (playfield size only — no UA).
 * Desktop presentation never calls this.
 */
export function resolveMobileCameraMode(
  playfieldWidth: number,
  playfieldHeight: number,
): MobileCameraMode {
  return playfieldHeight >= playfieldWidth ? 'follow' : 'fit';
}

/**
 * Mobile-only camera constants. Desktop SceneWorld framing must not read these.
 *
 * Portrait FOLLOW zooms past cover so the phone shows a crop of the fixed
 * mobile world and can pan; landscape FIT stays near cover and locks framing.
 */
export const MOBILE_CAMERA = {
  portraitFollow: {
    /**
     * Multiply cover zoom so vertical/horizontal pan room exists.
     * Extra overscan keeps the scaled plate covering the playfield even when
     * the camera sits at a pan extreme (Safari subpixel / vv jitter).
     */
    coverOverscan: 1.38,
    deadZoneViewportFracX: 0.55,
    deadZoneViewportFracY: 0.58,
  },
  landscapeFit: {
    /** Tiny overscan — full plate coverage, no follow pan. */
    coverOverscan: 1.02,
    /**
     * Locked landscape frame Y (0–1 world). Prefer the fenced upper yard /
     * back fence over the patio, but not so high that sky/forest dominate.
     * 0.53 ≈ midway between image center (0.50) and the prior patio-biased
     * clamp (~0.60): fence stays in, a little more patio/clothesline show.
     * Horizontal framing stays play-area centered.
     */
    fitAnchorY: 0.53,
    /**
     * Vertical band that landscape fit tries to keep on-screen (trees above
     * the fence through playable grass). Patio below may crop.
     */
    importantTop: 0.18,
    importantBottom: 0.88,
  },
} as const;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Raw cover zoom (no overscan) for a fixed world into a viewport. */
function rawCoverZoom(
  world: Pick<SceneWorld, 'widthPx' | 'heightPx'>,
  viewport: ViewportSize,
): number {
  return Math.max(
    viewport.width / Math.max(1, world.widthPx),
    viewport.height / Math.max(1, world.heightPx),
  );
}

/** Desktop design zoom from a SceneWorld (uses cached value when present). */
export function computeDesktopDesignZoom(world: SceneWorld): number {
  if (world.designZoom != null) return world.designZoom;
  return resolveDesktopDesignZoom(
    world.widthPx,
    world.heightPx,
    world.playArea,
  );
}

/**
 * Minimum zoom so the fixed world covers the viewport (no letterbox).
 * Tiny overscan avoids subpixel cream seams on some browsers.
 */
export function coverZoomForViewport(
  world: Pick<SceneWorld, 'widthPx' | 'heightPx'>,
  viewport: ViewportSize,
): number {
  return rawCoverZoom(world, viewport) * 1.002;
}

/**
 * CSS translate(%) for a fixed-pixel SceneWorld under
 * `translate(...) scale(zoom)` with transform-origin center.
 */
export function fixedWorldPanOffsetPercent(
  center: number,
  panStrength: number,
): number {
  return (0.5 - center) * 100 * panStrength;
}

/**
 * Clamp a fixed-world pan offset so the scaled plate always covers the
 * viewport when zoom is sufficient (framingSpan * zoom ≥ 1).
 */
export function clampFixedWorldPanOffsetPercent(
  offsetPercent: number,
  framingSpan: number,
  zoom: number,
): number {
  const span = Math.max(0.01, framingSpan);
  const z = Math.max(0.01, zoom);
  if (span * z < 1 - 1e-4) return 0;
  const flush = 50 - 50 / (span * z);
  return clamp(offsetPercent, -flush, flush);
}

function mobilePlayAreaFrameCenter(
  world: SceneWorld,
  zoom: number,
  worldWidth: number,
  worldHeight: number,
): { x: number; y: number } {
  const { halfW, halfH } = visibleWorldHalfExtents(
    zoom,
    worldWidth,
    worldHeight,
  );
  const playCenterX = (world.playArea.left + world.playArea.right) / 2;
  const playCenterY = (world.playArea.top + world.playArea.bottom) / 2;
  return {
    x: halfW >= 0.49 ? 0.5 : clamp(playCenterX, halfW, 1 - halfW),
    y: halfH >= 0.49 ? 0.5 : clamp(playCenterY, halfH, 1 - halfH),
  };
}

/**
 * Mobile landscape FIT framing — same zoom/cover as before, but Y is anchored
 * to {@link MOBILE_CAMERA.landscapeFit.fitAnchorY} so the back fence stays in
 * view (patio may crop). Portrait continues to use {@link mobilePlayAreaFrameCenter}.
 */
function mobileLandscapeFitFrameCenter(
  world: SceneWorld,
  zoom: number,
  worldWidth: number,
  worldHeight: number,
): { x: number; y: number } {
  const { halfW, halfH } = visibleWorldHalfExtents(
    zoom,
    worldWidth,
    worldHeight,
  );
  const playCenterX = (world.playArea.left + world.playArea.right) / 2;
  const { fitAnchorY, importantTop } = MOBILE_CAMERA.landscapeFit;

  const x = halfW >= 0.49 ? 0.5 : clamp(playCenterX, halfW, 1 - halfW);

  if (halfH >= 0.49) {
    return { x, y: 0.5 };
  }

  // Prefer the centralized anchor; if pan room is tight, still keep the
  // important top (trees / back fence) from leaving the viewport.
  const minY = halfH;
  const maxY = 1 - halfH;
  const keepTopVisible = importantTop + halfH;
  const preferred = Math.min(fitAnchorY, keepTopVisible);
  return { x, y: clamp(preferred, minY, maxY) };
}

/**
 * Frame a fixed SceneWorld into the current viewport.
 *
 * Desktop: locked design zoom (+ cover bump only if viewport outgrows it).
 *   — DESKTOP BRANCH IS LOCKED. Do not change.
 * Mobile: portrait FOLLOW (cropped cover + pan) or landscape FIT (static cover).
 *
 * World Editor must NOT call this — use {@link frameEditorOverview} instead.
 */
export function frameSceneWorld(
  world: SceneWorld,
  viewport: ViewportSize,
): WorldCameraFrame {
  const { worldWidth, worldHeight } = worldFactorsForViewport(
    world,
    viewport.width,
    viewport.height,
  );
  const config = { ...DEFAULT_RESPONSIVE_EXPLORE };

  if (world.cameraPolicy === 'mobile-cover') {
    const cameraMode = resolveMobileCameraMode(viewport.width, viewport.height);
    const cover = rawCoverZoom(world, viewport);

    if (cameraMode === 'fit') {
      // Landscape: same cover scale; lock framing biased up toward the fence.
      const zoom = cover * MOBILE_CAMERA.landscapeFit.coverOverscan;
      const frameCenter = mobileLandscapeFitFrameCenter(
        world,
        zoom,
        worldWidth,
        worldHeight,
      );
      return {
        zoom,
        focusZoom: zoom * config.focusZoomRelative,
        mode: 'fit',
        frameCenter,
        framingWidth: worldWidth,
        framingHeight: worldHeight,
        coverBumped: false,
        config,
      };
    }

    // Portrait: zoom past cover so the viewport is a window into the world
    // (pan room). Still ≥ cover → never letterbox / blank gutters.
    const zoom = cover * MOBILE_CAMERA.portraitFollow.coverOverscan;
    const frameCenter = mobilePlayAreaFrameCenter(
      world,
      zoom,
      worldWidth,
      worldHeight,
    );
    return {
      zoom,
      focusZoom: zoom * config.focusZoomRelative,
      mode: 'follow',
      frameCenter,
      framingWidth: worldWidth,
      framingHeight: worldHeight,
      coverBumped: false,
      config,
    };
  }

  // ——— DESKTOP (locked) ———
  const designZoom = computeDesktopDesignZoom(world);
  const coverMin = coverZoomForViewport(world, viewport);
  const coverBumped = coverMin > designZoom + 1e-6;
  // ~1% overscan: pan bias toward the play area must never open cream seams.
  const zoom = Math.max(designZoom, coverMin) * 1.01;

  const playCenterX = (world.playArea.left + world.playArea.right) / 2;
  const playCenterY = (world.playArea.top + world.playArea.bottom) / 2;
  const { halfW, halfH } = visibleWorldHalfExtents(
    zoom,
    worldWidth,
    worldHeight,
  );
  // When nearly exact-cover, lock to center — edge bias causes cream gutters.
  const centerX =
    halfW >= 0.49 ? 0.5 : clamp(playCenterX, halfW, 1 - halfW);
  const centerY =
    halfH >= 0.49 ? 0.5 : clamp(playCenterY, halfH, 1 - halfH);
  const frameCenter = { x: centerX, y: centerY };

  return {
    zoom,
    focusZoom: zoom * config.focusZoomRelative,
    mode: 'fit',
    frameCenter,
    framingWidth: worldWidth,
    framingHeight: worldHeight,
    coverBumped,
    config,
  };
}

/**
 * World Editor only — EDITOR_OVERVIEW camera.
 *
 * Desktop: contain-fit the entire fixed world so every prop is visible.
 * Aspect-ratio letterboxing is OK; gutters are filled by artwork bleed.
 *
 * Mobile / tablet (mobile-cover): cover-fit so the yard fills the playfield —
 * no pillarbox/letterbox side bars. Edges stay reachable via editor pan.
 *
 * Never uses Mobile FOLLOW / character-tracking. Padding defaults to 1 so
 * Reset View uses the full workspace with no intentional margin.
 */
export function frameEditorOverview(
  world: SceneWorld,
  viewport: ViewportSize,
  padding = 1,
): WorldCameraFrame {
  const { worldWidth, worldHeight } = worldFactorsForViewport(
    world,
    viewport.width,
    viewport.height,
  );
  const config = { ...DEFAULT_RESPONSIVE_EXPLORE };
  const pad = clamp(padding, 0.5, 1);
  const scaleX = viewport.width / Math.max(1, world.widthPx);
  const scaleY = viewport.height / Math.max(1, world.heightPx);
  const containZoom = Math.min(scaleX, scaleY) * pad;
  // Phone/tablet editor: fill the page (cover). Desktop keeps contain.
  const coverFill = world.cameraPolicy === 'mobile-cover';
  const zoom = coverFill ? Math.max(scaleX, scaleY) * pad : containZoom;
  return {
    zoom,
    focusZoom: zoom,
    mode: 'fit',
    frameCenter: { x: 0.5, y: 0.5 },
    framingWidth: worldWidth,
    framingHeight: worldHeight,
    coverBumped: coverFill && zoom > containZoom + 1e-6,
    config,
  };
}

/** Legacy helper kept for older call sites. */
export function worldCameraPanOffsets(
  centerX: number,
  centerY: number,
  framingWidth: number,
  framingHeight: number,
  zoom: number,
  panStrength: number,
): { offsetX: number; offsetY: number } {
  return {
    offsetX: clampCoverPanOffsetPercent(
      cameraPanTranslatePercent(centerX, framingWidth, zoom, panStrength),
      framingWidth,
      zoom,
    ),
    offsetY: clampCoverPanOffsetPercent(
      cameraPanTranslatePercent(centerY, framingHeight, zoom, panStrength),
      framingHeight,
      zoom,
    ),
  };
}

export { coverSafeCenterRange };
